import { graphql, useStaticQuery } from "gatsby"
import {
  createArticle,
  createPerson,
  makeAbsolute,
} from "@/components/schema/utils"
import { formatDateURL } from "@/libs/helpers"

const getArticlePath = (
  article: {
    category?: { name: string; _id: string } | null
    date: string
    slug: string | null
  },
  categories: {
    name: string
    slug: { current: string }
    group?: { slug: { current: string } } | null
  }[],
) => {
  const blockCategory = categories.find(
    cat => cat.name === article.category?.name,
  )
  const categoryGroupSlug = blockCategory?.group?.slug.current
  const categorySlug = blockCategory?.slug.current

  return `/${[
    categoryGroupSlug,
    categorySlug,
    formatDateURL(article.date),
    article.slug,
  ]
    .filter(Boolean)
    .join("/")}`
}

type AuthorSchemaProps = {
  name: string
  description: string
  imageUrl?: string | null
  articles?: Array<{
    _id: string
    author: string[]
    date: string
    elements: Array<Record<string, unknown> | null>
    slug: string | null
    category: {
      _id: string
      name: string
      slug: string
    } | null
    tlDrExcerpt: string | null
    images: Array<{
      publicURL: string
    } | null>
  }>
}

export const AuthorSchema = ({
  name,
  description,
  imageUrl,
  articles,
}: AuthorSchemaProps) => {
  const data = useStaticQuery(graphql`
    query Categories {
      allSanityBlockCategories {
        nodes {
          name
          slug {
            current
          }
          theme {
            rgb {
              r
              g
              b
            }
          }
          group {
            slug {
              current
            }
          }
        }
      }
    }
  `)

  const authorUrlPath = makeAbsolute(
    `/author/${name.toLowerCase().replace(/\s+/g, "-")}`,
  )

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      createPerson({
        name,
        description,
        imageUrl,
      }),
      {
        "@type": "WebPage",
        "@id": `${authorUrlPath}#webpage`,
        url: authorUrlPath,
        name: `${name} - Author at The DONUT`,
        description: description,
        about: {
          "@id": `${authorUrlPath}#person`,
        },
      },
      ...(articles?.map(article =>
        createArticle({
          title:
            (article.elements.find(elem => elem?.type === "Heading")
              ?.content as string) || "",
          description: article.tlDrExcerpt || "",
          url: makeAbsolute(
            getArticlePath(article, data.allSanityBlockCategories.nodes),
          ),
          imageUrl: article.images?.[0]?.publicURL,
          datePublished: article.date,
          authorName: name,
          useAuthorId: true,
        }),
      ) || []),
    ],
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  )
}
